export interface StemMix {
  muted: boolean;
  volume: number; // 0..1
  eq: { lowDb: number; midDb: number; highDb: number };
}

export type PlaybackState = "stopped" | "playing" | "paused" | "suspended";

export interface SectionGraph {
  sectionId: string;
  // Reconstruction ingredients — an AudioBufferSourceNode can only be start()ed
  // once, so `play()` rebuilds the graph from these every time.
  buffer: AudioBuffer;
  stems: readonly string[];
  order: number;
  mixes: Map<string, StemMix>;
  // Live nodes — recreated on every buildGraph() call.
  source: AudioBufferSourceNode;
  stemGains: Map<string, GainNode>;
  stemEqs: Map<string, BiquadFilterNode[]>; // [low, mid, high]
  masterGain: GainNode;
  durationMs: number;
}

export class PlaybackEngine {
  private graphs = new Map<string, SectionGraph>();
  private state: PlaybackState = "stopped";
  private startTime = 0;
  private resumeOffsetMs = 0;

  constructor(
    private readonly ctx: AudioContext,
    private readonly onStateChange?: (state: PlaybackState) => void,
  ) {}

  async loadSection(
    sectionId: string,
    audioBuffer: ArrayBuffer,
    stems: readonly string[],
    order: number,
  ): Promise<void> {
    const buffer = await this.ctx.decodeAudioData(audioBuffer.slice(0));
    this.graphs.set(sectionId, {
      sectionId,
      buffer,
      stems: [...stems],
      order,
      mixes: new Map<string, StemMix>(),
      // Placeholders — buildGraph fills the live nodes below.
      source: undefined as unknown as AudioBufferSourceNode,
      stemGains: new Map<string, GainNode>(),
      stemEqs: new Map<string, BiquadFilterNode[]>(),
      masterGain: undefined as unknown as GainNode,
      durationMs: buffer.duration * 1000,
    });
    this.buildGraph(sectionId);
  }

  // Builds the per-section audio graph (source -> per-stem EQ -> gain -> master
  // -> destination) and reapplies any stored stem mixes. Called by loadSection
  // and again by play() to recreate the consumed AudioBufferSourceNode.
  private buildGraph(sectionId: string): void {
    const g = this.graphs.get(sectionId);
    if (!g) return;
    const source = this.ctx.createBufferSource();
    source.buffer = g.buffer;
    const stemGains = new Map<string, GainNode>();
    const stemEqs = new Map<string, BiquadFilterNode[]>();
    const masterGain = this.ctx.createGain();
    masterGain.connect(this.ctx.destination);

    for (const stem of g.stems) {
      const gain = this.ctx.createGain();
      const low = this.ctx.createBiquadFilter();
      low.type = "lowshelf";
      low.frequency.value = 200;
      const mid = this.ctx.createBiquadFilter();
      mid.type = "peaking";
      mid.frequency.value = 1000;
      const high = this.ctx.createBiquadFilter();
      high.type = "highshelf";
      high.frequency.value = 4000;
      source.connect(low).connect(mid).connect(high).connect(gain).connect(masterGain);
      stemGains.set(stem, gain);
      stemEqs.set(stem, [low, mid, high]);
    }
    if (g.stems.length === 0) source.connect(masterGain);

    g.source = source;
    g.stemGains = stemGains;
    g.stemEqs = stemEqs;
    g.masterGain = masterGain;

    // Reapply any mix captured before the rebuild so the mixer state survives
    // pause/play (which consumes the old nodes).
    for (const [stem, mix] of g.mixes) this.applyMixToNodes(g, stem, mix);
  }

  applyStemMix(sectionId: string, stem: string, mix: StemMix): void {
    const g = this.graphs.get(sectionId);
    if (!g) return;
    // Persist the mix so buildGraph can reapply it after a rebuild.
    g.mixes.set(stem, mix);
    this.applyMixToNodes(g, stem, mix);
  }

  private applyMixToNodes(g: SectionGraph, stem: string, mix: StemMix): void {
    const gain = g.stemGains.get(stem);
    if (gain) gain.gain.value = mix.muted ? 0 : mix.volume;
    const eqs = g.stemEqs.get(stem);
    if (eqs) {
      eqs[0]!.gain.value = mix.eq.lowDb;
      eqs[1]!.gain.value = mix.eq.midDb;
      eqs[2]!.gain.value = mix.eq.highDb;
    }
  }

  // Sections ordered by Section.order — the song is the concatenation of its
  // sections in that order (same criterion as the Timeline).
  private orderedGraphs(): SectionGraph[] {
    return [...this.graphs.values()].sort((a, b) => a.order - b.order);
  }

  getTotalMs(): number {
    let total = 0;
    for (const g of this.graphs.values()) total += g.durationMs;
    return total;
  }

  private setState(state: PlaybackState): void {
    this.state = state;
    this.onStateChange?.(state);
  }

  async play(): Promise<void> {
    if (this.ctx.state === "suspended") {
      this.setState("suspended");
      await this.ctx.resume();
    }
    const ordered = this.orderedGraphs();
    const totalMs = this.getTotalMs();
    if (ordered.length === 0 || totalMs === 0) {
      // Nothing to play — settle back to stopped immediately.
      this.finish();
      return;
    }

    const resumeOffsetMs = Math.min(Math.max(0, this.resumeOffsetMs), totalMs);
    if (resumeOffsetMs >= totalMs) {
      this.finish();
      return;
    }

    const offsetSec = resumeOffsetMs / 1000;
    this.startTime = this.ctx.currentTime - offsetSec;

    let sectionStartMs = 0;
    let lastSource: AudioBufferSourceNode | null = null;
    let lastEndMs = -1;
    for (const g of ordered) {
      const sectionEndMs = sectionStartMs + g.durationMs;
      // Rebuild the source/graph — the prior AudioBufferSourceNode (if any) was
      // consumed by an earlier play()/pause() and cannot be reused.
      this.buildGraph(g.sectionId);
      const source = g.source;

      if (sectionEndMs <= resumeOffsetMs) {
        // Section already finished before the resume point — do not schedule.
      } else if (sectionStartMs <= resumeOffsetMs) {
        // Section contains the resume point — start now from its internal offset.
        const intraSec = (resumeOffsetMs - sectionStartMs) / 1000;
        source.start(this.ctx.currentTime, intraSec);
      } else {
        // Section starts in the future — schedule at its absolute start instant.
        source.start(this.startTime + sectionStartMs / 1000);
      }

      if (sectionEndMs > resumeOffsetMs && sectionEndMs > lastEndMs) {
        lastEndMs = sectionEndMs;
        lastSource = source;
      }
      sectionStartMs = sectionEndMs;
    }

    // When the last scheduled section ends, settle back to stopped.
    if (lastSource) {
      lastSource.onended = (): void => {
        if (this.state === "playing") this.finish();
      };
    }

    this.setState("playing");
  }

  pause(): void {
    if (this.state !== "playing") return;
    // resumeOffsetMs is the GLOBAL song position, not a per-section offset.
    this.resumeOffsetMs = (this.ctx.currentTime - this.startTime) * 1000;
    this.stopAllSources();
    this.setState("paused");
  }

  seek(ms: number): void {
    const clamped = Math.min(Math.max(0, ms), this.getTotalMs());
    const wasPlaying = this.state === "playing";
    if (wasPlaying) {
      // Stop the current sources without losing the "playing" intent, then
      // reschedule from the new point.
      this.stopAllSources();
      this.state = "paused";
    }
    this.resumeOffsetMs = clamped;
    if (wasPlaying) void this.play();
  }

  // Internal terminal transition — last section ended (or nothing to play).
  private finish(): void {
    this.stopAllSources();
    this.resumeOffsetMs = 0;
    this.setState("stopped");
  }

  private stopAllSources(): void {
    for (const g of this.graphs.values()) {
      g.source.onended = null;
      try {
        g.source.stop();
      } catch {
        /* not started */
      }
    }
  }

  getCurrentMs(): number {
    if (this.state === "playing") {
      return Math.min((this.ctx.currentTime - this.startTime) * 1000, this.getTotalMs());
    }
    return this.resumeOffsetMs;
  }

  /**
   * Pure read-only getter (Plan 13, Task 5.2): which section is sounding now,
   * and the position inside it. Reuses the same accumulated-offset math as
   * `play()` — sections are concatenated in `order`, so the section that owns
   * the global instant `getCurrentMs()` is the one whose `[start, end)` window
   * contains it. Does NOT touch audio scheduling.
   *
   * Returns `null` when there is no audio or the playhead is exactly at the
   * end of the song (`intraMs` would have no section to belong to).
   */
  getActiveSection(): { sectionId: string; intraMs: number } | null {
    const ordered = this.orderedGraphs();
    if (ordered.length === 0) return null;
    const nowMs = Math.min(Math.max(0, this.getCurrentMs()), this.getTotalMs());
    let sectionStartMs = 0;
    for (const g of ordered) {
      const sectionEndMs = sectionStartMs + g.durationMs;
      // `[start, end)` — a half-open window so a section boundary belongs to
      // the next section. The last section also claims its own end instant so
      // the playhead resting at the song's end still maps to a real section.
      const isLast = g === ordered[ordered.length - 1];
      const within =
        nowMs >= sectionStartMs && (nowMs < sectionEndMs || (isLast && nowMs <= sectionEndMs));
      if (within) {
        return { sectionId: g.sectionId, intraMs: nowMs - sectionStartMs };
      }
      sectionStartMs = sectionEndMs;
    }
    return null;
  }

  getState(): PlaybackState {
    return this.state;
  }

  dispose(): void {
    this.stopAllSources();
    this.graphs.clear();
  }
}
